
import { Button } from '@/components/ui/button';
import { Edit, Activity, Camera, Trash2 } from 'lucide-react';
import { NutritionButton } from '@/components/nutrition';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePets } from '../hooks/usePets';
import { useAuth } from '../contexts/AuthContext';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface PetProfileActionsProps {
  pet: {
    id: string;
    name: string;
    user_id?: string;
  };
}

const PetProfileActions = ({ pet }: PetProfileActionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const { deletePet } = usePets();
  
  // Check if current user owns this pet
  const isOwner = user?.id === pet?.user_id;

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      console.log('🗑️ Attempting to delete pet:', pet.id);
      
      await new Promise((resolve, reject) => {
        deletePet(pet.id, {
          onSuccess: () => {
            console.log('🗑️ Pet deleted successfully:', pet.id);
            navigate('/my-pets');
            resolve(true);
          },
          onError: (error) => {
            console.error('🗑️ Error deleting pet:', error);
            reject(error);
          }
        });
      });
    } catch (error) {
      console.error('🗑️ Delete operation failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Don't render anything if user is not the owner
  if (!isOwner) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Row 1: Edit */}
      <div className="flex">
        <Button
          onClick={() => navigate(`/edit-pet/${pet.id}`)}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <Edit className="h-4 w-4" />
          {t('common.edit')}
        </Button>
      </div>

      {/* Row 2: Nutrition, Medical Records, Create Memory */}
      <div className="flex flex-wrap gap-2">
        <NutritionButton petId={pet.id} petName={pet.name} />
        
        <Button
          onClick={() => navigate(`/medical/${pet.id}`)}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <Activity className="h-4 w-4" />
          {t('navigation.medicalRecords')}
        </Button>

        <Button
          onClick={() => navigate(`/create-post?petId=${pet.id}`)}
          variant="default"
          size="sm"
          className="flex items-center gap-2"
        >
          <Camera className="h-4 w-4" />
          {t('feed.createMemory')}
        </Button>
      </div>

      {/* Row 3: Delete (isolated for safety) */}
      <div className="flex">
        <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button 
            variant="outline"
            size="sm"
            className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4" />
            {t('common.delete')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('modals.deleteConfirm.deletePet', { petName: pet.name })}</AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-2">
              <p><strong>{t('modals.deleteConfirm.deletePetWarning')}</strong></p>
              <p>{t('modals.deleteConfirm.deletePetDescription', { petName: pet.name })}:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>{t('modals.deleteConfirm.petInfo', { petName: pet.name })}</li>
                <li>{t('modals.deleteConfirm.petPhotos')}</li>
                <li>{t('modals.deleteConfirm.petPosts')}</li>
                <li>{t('modals.deleteConfirm.medicalRecords')}</li>
                <li>{t('modals.deleteConfirm.commentsLikes')}</li>
              </ul>
              <p className="text-red-600 font-medium">{t('modals.deleteConfirm.cannotRecover')}</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('modals.deleteConfirm.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              disabled={isDeleting}
            >
              {isDeleting ? t('modals.deleteConfirm.deleting') : t('modals.deleteConfirm.deletePetButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default PetProfileActions;
